import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import agLogo from "./sales/ag-logo.png";
import { useLocation } from "react-router-dom";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import StorefrontIcon from "@mui/icons-material/Storefront";
import GroupsIcon from "@mui/icons-material/Groups";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import BadgeIcon from "@mui/icons-material/Badge";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import TableViewIcon from "@mui/icons-material/TableView";
import VisibilityIcon from "@mui/icons-material/Visibility";

export default function Dashboard({ logout }) {
  const navigate = useNavigate();

  const currentUser = JSON.parse(localStorage.getItem("currentUser")) || {};
  const role = currentUser?.role?.toLowerCase() || "guest";
  const normalizedAccess = (() => {
    try {
      const raw = currentUser?.access;
      if (Array.isArray(raw)) return raw.map((item) => String(item).toLowerCase().replace(/\s+/g, ""));
      if (typeof raw === "string") {
        const parsed = raw.trim().startsWith("[") ? JSON.parse(raw) : raw.split(",");
        return parsed.map((item) => String(item).toLowerCase().replace(/\s+/g, ""));
      }
      return [];
    } catch {
      return [];
    }
  })();

  const [timeNow, setTimeNow] = useState(new Date());
  const [search, setSearch] = useState("");
  const location = useLocation();

  useEffect(() => {
    const timer = setInterval(() => setTimeNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const shortcuts = [
    {
      name: "Sales By Customer",
      accessKey: "salesbyitems",
      icon: <PointOfSaleIcon fontSize="inherit" />,
      desc: "Sales report by customer",
      path: "/salesbyitems",
      color: "#16a34a",
    },
    {
      name: "Sales By Items",
      accessKey: "salesitems1",
      icon: <Inventory2Icon fontSize="inherit" />,
      desc: "Sales report by items",
      path: "/SalesItems1",
      color: "#2563eb",
    },

    {
      name: "Purchase By Items",
      accessKey: "purchaseitems",
      icon: <ShoppingCartIcon fontSize="inherit" />,
      desc: "Purchase report by items",
      path: "/PurchaseItems",
      color: "#f97316",
    },

    {
      name: "Purchase By Supplier",
      accessKey: "purchasebycustomer",
      icon: <StorefrontIcon fontSize="inherit" />,
      desc: "Purchase report by supplier",
      path: "/purchasebycustomer",
      color: "#7c3aed",
    },

    {
      name: "Purchaser Report",
      accessKey: "purchaserreport",
      icon: <GroupsIcon fontSize="inherit" />,
      desc: "Purchaser wise summary report",
      path: "/PurchaserReport",
      color: "#dc2626",
    },

    {
      name: "Monthly Budget",
      accessKey: "monthlybudget1",
      icon: <CalendarMonthIcon fontSize="inherit" />,
      desc: "Monthly budget planning and report",
      path: "/MonthlyBudget1",
      color: "#0f766e",
    },

    {
      name: "Employee Report",
      accessKey: "employeereport",
      icon: <BadgeIcon fontSize="inherit" />,
      desc: "Employee report and tracking",
      path: "/EmpGapForm",
      color: "#9333ea",
    },

    {
      name: "Stock Entry",
      accessKey: "stockentry",
      icon: <WarehouseIcon fontSize="inherit" />,
      desc: "Stock entry and inventory",
      path: "/StockForm",
      color: "#0ea5e9",
    },

    {
      name: "Roles",
      accessKey: "roles",
      icon: <AdminPanelSettingsIcon fontSize="inherit" />,
      desc: "Manage roles and access",
      path: "/UserAccessControl",
      color: "#111827",
    },

    {
      name: "Item Table",
      accessKey: "itemtable",
      icon: <TableViewIcon fontSize="inherit" />,
      desc: "Item pricing and master table",
      path: "/ItemTable",
      color: "#1d4ed8",
    },
    
    {
      name: "Item Table Viewer",
      accessKey: "itemtableviewer",
      icon: <VisibilityIcon fontSize="inherit" />,
      desc: "Read-only item table viewer",
      path: "/ItemTableViewer",
      color: "#0891b2",
    },
  ];

  const formattedDate = timeNow.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  const formattedTime = timeNow.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

  const capitalizeFirst = (name) => {
    if (!name) return "";
    return name.charAt(0).toUpperCase() + name.slice(1);
  };

  const availableShortcuts = shortcuts
    .filter((item) => {
      if (role === "owner" || role === "admin") return true;
      if (role === "employeeform") return item.accessKey === "employeereport";
      if (role === "transfer") return item.accessKey === "stockentry";
      if (role === "pdfviewer") return item.accessKey === "itemtableviewer";
      if (role === "itemadmin") return item.accessKey === "itemtable";
      if (item.accessKey === "roles") return false;
      return normalizedAccess.includes(item.accessKey);
    });
  const filteredShortcuts = availableShortcuts.filter((item) => item.name.toLowerCase().includes(search.toLowerCase().trim()));

  return (
    <div className="portal-dashboard">
      <div className="portal-page-heading">
        <div><span className="portal-eyebrow">YOUR WORKSPACE</span><h1>Business overview</h1><p>Your reports and operations, together in one place.</p></div>
        <div className="portal-date-badge"><CalendarMonthIcon /><span>{formattedDate}<small>{formattedTime}</small></span></div>
      </div>
      <section className="portal-welcome-card" aria-labelledby="portal-welcome-title">
        <div className="portal-welcome-copy"><span className="portal-welcome-kicker">ALI GHOLAMI REPORTS</span><h2 id="portal-welcome-title">Welcome back,<br />{capitalizeFirst(currentUser?.username || "User")}.</h2><p>Find the detail. See the bigger picture.<br />Choose a report below to get started.</p></div>
        <div className="portal-welcome-decoration" aria-hidden="true"><div className="portal-orbit orbit-one" /><div className="portal-orbit orbit-two" /><div className="portal-orbit orbit-three" /><div className="portal-welcome-mark"><img src={agLogo} alt="" /></div></div>
        <div className="portal-welcome-meta"><span className="portal-status-dot" /><span>{availableShortcuts.length} modules available in your workspace</span></div>
      </section>
      <section aria-labelledby="portal-modules-title">
        <div className="portal-section-heading"><div><h2 id="portal-modules-title">Reports & operations <span className="portal-count-badge">{availableShortcuts.length}</span></h2><p>Everything you need for your day-to-day work.</p></div><label className="portal-module-search"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6.5" /><path d="m16 16 4.5 4.5" /></svg><input type="search" aria-label="Search modules" placeholder="Find a report or module…" value={search} onChange={(event) => setSearch(event.target.value)} /></label></div>
        <div className="portal-modules-grid">{filteredShortcuts.map((item) => <button key={item.path} className="portal-module-card" onClick={() => navigate(item.path)}><span className="portal-module-icon" style={{ "--module-accent": item.color }}>{item.icon}</span><span className="portal-module-content"><strong>{item.name}</strong><span>{item.desc}</span></span><span className="portal-module-arrow" aria-hidden="true">↗</span></button>)}</div>
        {!filteredShortcuts.length && <div className="portal-empty-modules"><strong>{availableShortcuts.length ? "No matching modules" : "No modules available"}</strong><p>{availableShortcuts.length ? "Try a different name to find your report." : "Your assigned modules will appear here."}</p>{search && <button className="portal-text-button" onClick={() => setSearch("")}>Clear search</button>}</div>}
      </section>
      <footer className="portal-dashboard-footer"><span>Ali Gholami Trading · Reports workspace</span><button className="portal-text-button" onClick={logout}>Sign out</button></footer>
    </div>
  );
}
